import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useContext, useEffect, useState } from 'react';
import { Settings } from '../../../../modules/settings/SettingsContext';
import { ICurrentWorkspaceStore } from '../../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { useLoader } from '../../../helpers/useLoader';
import { DatasetsList } from '../components/DatasetsList';
import { DeleteDatasetConfirmationModal } from '../components/DeleteDatasetConfirmationModal';
import { Datasets } from '../datasets.context';
import { IImagesUploaderService } from '../imagesUpload.service';

export const DatasetsListContainer = observer((): ReactElement => {
  const imagesUploaderService = useInjection(IImagesUploaderService.$);
  const currentWorkspaceStore = useInjection(ICurrentWorkspaceStore.$);

  const { isLoading, withLoaderAsync } = useLoader();
  const datasetsContext = useContext(Datasets);
  const settingsContext = useContext(Settings);

  const [showDatasetDeleteConfirmationModal, setShowDatasetDeleteConfirmationModal] = useState(false);
  const [datasetIdToDelete, setDatasetIdToDelete] = useState('');

  useEffect(() => {
    void datasetsContext.service.loadUserDatasetAsync();
  }, []);

  const handleDeleteDataset = async (): Promise<void> => {
    await withLoaderAsync(async () => {
      await Promise.allSettled([imagesUploaderService.cancelUpload(datasetIdToDelete), datasetsContext.service.deleteDatasetAsync(datasetIdToDelete)]);
      setDatasetIdToDelete('');
      setShowDatasetDeleteConfirmationModal(false);
    });
  };

  const toggleDatasetDeleteConfirmationModal = (datasetId: string): void => {
    setShowDatasetDeleteConfirmationModal((prevState) => !prevState);
    setDatasetIdToDelete(datasetId);
  };

  const handleOrderChange = async (orderBy: string, orderType: string): Promise<void> => {
    await withLoaderAsync(async () => {
      await datasetsContext.service.changeOrder(orderBy, orderType);
    });
  };

  const handlePaginationChange = async (pageNumber: number, pageSize: number): Promise<void> => {
    await withLoaderAsync(async () => {
      settingsContext.setDatasetsPageSize(pageSize);
      await datasetsContext.service.paginationChange(pageNumber, pageSize);
    });
  };

  const paging = { ...datasetsContext.store.datasetsPaging, pageSize: settingsContext.store.datasetsPageSize };

  return (
    <>
      <DatasetsList
        datasetsList={datasetsContext.store.datasets}
        workspaceId={currentWorkspaceStore.currentWorkspace.id}
        isLoading={datasetsContext.store.stale || isLoading}
        onShowDatasetDeleteConfirmationModal={toggleDatasetDeleteConfirmationModal}
        datasetsPaging={paging}
        onOrderingChange={handleOrderChange}
        onPaginationChange={handlePaginationChange}
      />
      <DeleteDatasetConfirmationModal
        showDatasetDeleteConfirmationModal={showDatasetDeleteConfirmationModal}
        onDeleteDatasetCancel={(): void => {
          toggleDatasetDeleteConfirmationModal('');
        }}
        onDatasetDeleteConfirm={handleDeleteDataset}
        datasetDeleteConfirmationText={datasetsContext.store.datasets.find((d) => d.id === datasetIdToDelete)?.name || ''}
      />
    </>
  );
});
